import React, { useState, useEffect } from 'react'
import { CheckCircle, ChevronLeft, ChevronRight, SlidersHorizontal } from 'lucide-react'
import { Link } from 'react-router-dom'

interface Offer {
  id: number
  title: string
  description: string
  click_url: string
  image: string
  cta_yes: string
  cta_no: string
  advertiser_name: string
  short_description: string
  short_headline: string
}

interface OffersResponse {
  data: {
    offers: Offer[]
  }
}

const ShopifyThankYouPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const [offers, setOffers] = useState<Offer[]>([])
  const [loading, setLoading] = useState(true)
  type SelectedOption = 'offer_ui' | 'offer_ui_below_total' | 'offer_ui_between_product_and_totals' | 'offer_ui_above_continue_shopping' | 'offer_ui_above_order_details' | 'offer_ui_above_map'
  const [selectedOption, setSelectedOption] = useState<SelectedOption>('offer_ui')
  const [showTooltip, setShowTooltip] = useState(false)

  // Fetch offers from Moments API
  const fetchOffers = async () => {
    try {
      setLoading(true)
      const response = await fetch('https://api.adspostx.com/native/v2/offers.json?api_key=582c2b53-56d5-49a2-a239-7c36e5b0c789', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          country: 'us'
        })
      })

      if (!response.ok) {
        throw new Error('Failed to fetch offers')
      }

      const data: OffersResponse = await response.json()
      setOffers(data.data.offers.slice(0, 3)) // Limit to 3 offers
      setLoading(false)
    } catch (error) {
      console.error('Error fetching offers:', error)
      // Fallback to demo offers
      setOffers([
        {
          id: 1,
          title: 'FREE 14-Day Trial! Discover Roots With Ancestry',
          description: 'Explore your family history with a 14-day free trial. Ancestry membership grants access to billions of records. Discover your roots today!',
          short_headline: 'FREE 14-Day Trial! Discover Roots With Ancestry',
          short_description: 'Explore your family history with a 14-day free trial. Ancestry membership grants access to billions of records. Discover your roots today!',
          image: 'https://via.placeholder.com/80x80/9333EA/white?text=A',
          click_url: '#',
          advertiser_name: 'Ancestry',
          cta_yes: 'Claim Offer!',
          cta_no: 'No, Thanks'
        }
      ])
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchOffers()
  }, [])

  // Get current offer based on pagination
  const currentOffer = offers[currentPage - 1]

  const handleOfferClick = (offer: Offer) => {
    if (offer.click_url && offer.click_url !== '#') {
      window.open(offer.click_url, '_blank')
    }
  }

  // Reusable Offers Panel JSX
  const renderOfferPanel = () => (
    <div className="mb-8">
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
          <h3 className="text-lg font-semibold text-gray-900">Your order is complete</h3>
        </div>

        <div className="px-6 py-5">
          <p className="text-sm text-gray-600 mb-3">Here are some offers from brands you love!</p>
          {loading ? (
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                <div className="h-3 bg-gray-200 rounded"></div>
                <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                <div className="h-10 bg-gray-200 rounded mt-4"></div>
                <div className="h-9 bg-gray-200 rounded"></div>
              </div>
            </div>
          ) : currentOffer ? (
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div className="pr-4">
                  <h4 className="text-base font-semibold text-gray-900 mb-2 leading-tight">{currentOffer.short_headline}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed mb-3 max-w-md">
                    {currentOffer.short_description}
                  </p>
                </div>
                <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-md border border-gray-200 bg-white flex items-center justify-center overflow-hidden">
                  {currentOffer.image ? (
                    <img
                      src={currentOffer.image}
                      alt={currentOffer.advertiser_name}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                  ) : (
                    <span className="text-gray-500 text-xs font-medium">
                      {currentOffer.advertiser_name?.slice(0, 8) || 'Offer'}
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-2.5 mt-4">
                <button
                  onClick={() => handleOfferClick(currentOffer)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-3 rounded-md shadow-sm transition-colors"
                >
                  {currentOffer.cta_yes || 'Unlock Offer!'}
                </button>
                <button
                  className="w-full text-blue-600 hover:text-blue-700 bg-white border-2 border-gray-200 text-sm font-medium py-2.5 rounded-md transition-colors"
                >
                  {currentOffer.cta_no || 'No, Thanks'}
                </button>
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-gray-200 rounded-xl p-6 text-center text-sm text-gray-500">
              No offers available at this time.
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 bg-gray-50 px-6 py-3">
          <div className="flex items-center justify-center gap-3 text-gray-700">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-full hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
              disabled={currentPage === 1}
              aria-label="Previous offer"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {[1, 2, 3].map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-medium transition-colors border ${
                  currentPage === page
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-100'
                } ${page > offers.length ? 'opacity-40 cursor-not-allowed' : ''}`}
                disabled={page > offers.length && offers.length > 0}
                aria-label={`Offer ${page}`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage(Math.min(Math.max(offers.length, 3), currentPage + 1))}
              className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-full hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
              disabled={currentPage >= Math.max(offers.length, 3)}
              aria-label="Next offer"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
            <span>Powered by <span className="font-medium text-gray-700">MomentScience</span></span>
            <a href="https://momentscience.com/end-user-privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700">Privacy Policy</a>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/shopify-demo" className="flex items-center text-gray-600 hover:text-gray-800 transition-colors">
              <ChevronLeft className="w-5 h-5 mr-1" />
              <span className="text-sm font-medium">Back to Shopify Demo Home</span>
            </Link>
            <h1 className="text-xl font-semibold text-gray-900">MomentScience Swag Store</h1>
            <div className="w-48"></div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
          {/* Left Column - Thank You Message & Details */}
          <div className="px-6 py-8 lg:px-12 lg:py-12">
            <div className="max-w-lg mx-auto">
              
              {/* Thank You Section */}
              <div className="mb-6">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-8 h-8 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-600">Confirmation #ABC123EXAMPLE</p>
                    <h2 className="text-2xl font-semibold text-gray-900">Thank you, Frederick!</h2>
                  </div>
                </div>
              </div>

              {/* Offer UI above Map */}
              {selectedOption === 'offer_ui_above_map' && renderOfferPanel()}

              {/* Map Section */}
              <div className="mb-6">
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden relative h-52">
                  {/* Map placeholder with streets pattern */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-green-50 to-blue-100">
                    {/* Vertical streets */}
                    <div className="absolute top-0 left-1/4 w-0.5 h-full bg-gray-300 opacity-40"></div>
                    <div className="absolute top-0 left-2/4 w-1 h-full bg-gray-300 opacity-50"></div>
                    <div className="absolute top-0 left-3/4 w-0.5 h-full bg-gray-300 opacity-40"></div>
                    
                    {/* Horizontal streets */}
                    <div className="absolute top-1/4 left-0 h-0.5 w-full bg-gray-300 opacity-40"></div>
                    <div className="absolute top-2/4 left-0 h-1 w-full bg-gray-300 opacity-50"></div>
                    <div className="absolute top-3/4 left-0 h-0.5 w-full bg-gray-300 opacity-40"></div>
                    
                    {/* Map landmarks */}
                    <div className="absolute top-6 left-6 text-[10px] text-gray-500 font-medium">GEORGETOWN</div>
                    <div className="absolute bottom-6 right-6 text-[10px] text-gray-500 font-medium">BLOOMINGDALE</div>
                    <div className="absolute top-1/3 left-1/3 text-[10px] text-gray-500 font-medium">National Mall</div>
                    
                    {/* Location marker */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                      <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-lg">
                        <div className="w-3 h-3 bg-white rounded-full"></div>
                      </div>
                    </div>
                    
                    {/* Google Maps attribution */}
                    <div className="absolute bottom-2 left-2 bg-white/90 rounded px-1.5 py-0.5 text-[9px] text-gray-500 flex items-center gap-1">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor"/>
                      </svg>
                      <span>Map</span>
                    </div>
                  </div>
                  
                  {/* Address overlay */}
                  <div className="absolute top-4 right-4 bg-white rounded-md px-3 py-2 shadow-md border border-gray-200">
                    <div className="text-xs text-gray-600">Shipping address</div>
                    <div className="text-sm font-semibold text-gray-900">Washington DC</div>
                  </div>
                </div>
              </div>

              {/* Order Confirmed Section */}
              <div className="mb-6">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-base font-semibold text-gray-900 mb-1">Your order is confirmed</h3>
                  <p className="text-sm text-gray-700">You'll receive a confirmation email soon</p>
                </div>
              </div>

              {/* Offer UI above Order Details */}
              {selectedOption === 'offer_ui_above_order_details' && renderOfferPanel()}

              {/* Order Details Section */}
              <div className="mb-8">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-base font-semibold text-gray-900 mb-6">Order details</h3>
                  
                  <div className="grid grid-cols-2 gap-x-8 gap-y-6 mb-6">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Contact information</h4>
                      <p className="text-sm text-gray-900">frederick.jerde@example.com</p>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Payment method</h4>
                      <div className="flex items-center gap-2 text-sm text-gray-900">
                        <span className="text-gray-400">— — — —</span>
                        <span>Credit card</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-x-8 gap-y-6 mb-6">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Shipping address</h4>
                      <div className="text-sm text-gray-900 space-y-0.5">
                        <p>Frederick Jerde</p>
                        <p>1600 Pennsylvania Avenue NW</p>
                        <p>Washington DC 20500-0005</p>
                        <p>United States</p>
                        <p>+1 (202) 456-1414</p>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Billing address</h4>
                      <div className="text-sm text-gray-900 space-y-0.5">
                        <p>Frederick Jerde</p>
                        <p>1600 Pennsylvania Avenue NW</p>
                        <p>Washington DC 20500-0005</p>
                        <p>United States</p>
                        <p>+1 (202) 456-1414</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Shipping method</h4>
                    <p className="text-sm text-gray-900">Standard (Example)</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              {selectedOption === 'offer_ui_above_continue_shopping' && renderOfferPanel()}
              
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="text-left">
                    <span className="text-sm text-gray-600">Need help? </span>
                    <a href="#" className="text-sm text-blue-600 hover:text-blue-700 underline">
                      Contact us
                    </a>
                  </div>
                  <Link
                    to="/"
                    className="bg-blue-600 text-white py-3 px-6 rounded-md font-medium text-center hover:bg-blue-700 transition-colors whitespace-nowrap"
                  >
                    Continue shopping
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Offers & Order Summary */}
          <div className="bg-gray-50 border-l border-gray-200 px-6 py-8 lg:px-12 lg:py-12">
            <div className="max-w-lg mx-auto">
              {(() => {
                // Reusable Offers Panel JSX
                const offerPanel = renderOfferPanel()

                const productItem = (
                  <div className="mb-8">
                    <div className="flex items-start space-x-4">
                      <div className="relative">
                        <div className="w-16 h-16 bg-white border border-gray-300 rounded-lg flex items-center justify-center overflow-hidden">
                          <img 
                            src="https://images.unsplash.com/photo-1584100936595-c0654b55a2e2?w=200&h=200&fit=crop" 
                            alt="MomentScience Pillow"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className="absolute -top-2 -right-2 bg-gray-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                          1
                        </span>
                      </div>
                      <div className="flex-1 flex justify-between">
                        <div>
                          <h3 className="font-medium text-gray-900 mb-1">MomentScience Pillow</h3>
                          <p className="text-sm text-gray-600">White / Standard</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900">$20.00</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )

                const orderSummary = (
                  <div className="border-t border-gray-300 pt-6 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="text-gray-900">$20.00</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Shipping</span>
                      <span className="text-gray-900">$10.00</span>
                    </div>
                    <div className="flex justify-between items-center text-lg font-medium border-t border-gray-300 pt-2">
                      <span className="text-gray-900">Total</span>
                      <div className="text-right">
                        <span className="text-sm text-gray-600 mr-2">USD</span>
                        <span className="text-xl font-bold text-gray-900">$30.00</span>
                      </div>
                    </div>
                  </div>
                )

                return (
                  <>
                    {selectedOption === 'offer_ui' && offerPanel}
                    {productItem}
                    {selectedOption === 'offer_ui_between_product_and_totals' && offerPanel}
                    {orderSummary}
                    {selectedOption === 'offer_ui_below_total' && offerPanel}
                  </>
                )
              })()}

            </div>
          </div>
        </div>
      </div>

      {/* Hover-Triggered Options Panel - Left Side */}
      <div className="fixed left-0 top-1/3 -translate-y-1/2 z-40 group">
        {/* Hover trigger area */}
        <div className="w-8 h-32 bg-blue-600 rounded-r-lg flex items-center justify-center cursor-pointer hover:bg-blue-700 transition-colors shadow-lg">
          <SlidersHorizontal className="w-5 h-5 text-white" />
        </div>
        
        {/* Sliding panel - appears on hover */}
        <div className="absolute left-0 top-0 w-72 bg-white border-r border-gray-200 shadow-xl overflow-y-auto max-h-[80vh] opacity-0 -translate-x-full group-hover:opacity-100 group-hover:translate-x-8 transition-all duration-300 ease-out rounded-r-lg">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4" />
              Layout Options
            </h4>
            <div 
              className="relative"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-xs font-bold cursor-help">
                ?
              </div>
              {showTooltip && (
                <div className="absolute right-0 top-7 w-48 bg-gray-900 text-white text-xs rounded-lg p-2 shadow-lg z-50">
                  Click any layout to see how offers appear in different positions
                  <div className="absolute -top-1 right-2 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                </div>
              )}
            </div>
          </div> {/* Close flex header div */}
          
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-3">Choose Offer Placement</div>
            
            {/* Layout options */}
            <button
              onClick={() => setSelectedOption('offer_ui')}
              className={`w-full text-left px-3 py-3 rounded-lg border transition-all ${
                selectedOption === 'offer_ui'
                  ? 'border-blue-600 bg-blue-50 text-blue-800 shadow-md'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
              }`}
              aria-pressed={selectedOption === 'offer_ui'}
            >
              <div className="font-medium text-sm">Offer UI</div>
              <div className="text-xs text-gray-600 mt-1">Top of right column</div>
            </button>

            <button
              onClick={() => setSelectedOption('offer_ui_below_total')}
              className={`w-full text-left px-3 py-3 rounded-lg border transition-all ${
                selectedOption === 'offer_ui_below_total'
                  ? 'border-blue-600 bg-blue-50 text-blue-800 shadow-md'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
              }`}
              aria-pressed={selectedOption === 'offer_ui_below_total'}
            >
              <div className="font-medium text-sm">Below Total</div>
              <div className="text-xs text-gray-600 mt-1">Under order totals</div>
            </button>

            <button
              onClick={() => setSelectedOption('offer_ui_between_product_and_totals')}
              className={`w-full text-left px-3 py-3 rounded-lg border transition-all ${
                selectedOption === 'offer_ui_between_product_and_totals'
                  ? 'border-blue-600 bg-blue-50 text-blue-800 shadow-md'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
              }`}
              aria-pressed={selectedOption === 'offer_ui_between_product_and_totals'}
            >
              <div className="font-medium text-sm">Between Product & Total</div>
              <div className="text-xs text-gray-600 mt-1">After item, before totals</div>
            </button>

            <button
              onClick={() => setSelectedOption('offer_ui_above_continue_shopping')}
              className={`w-full text-left px-3 py-3 rounded-lg border transition-all ${
                selectedOption === 'offer_ui_above_continue_shopping'
                  ? 'border-blue-600 bg-blue-50 text-blue-800 shadow-md'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
              }`}
              aria-pressed={selectedOption === 'offer_ui_above_continue_shopping'}
            >
              <div className="font-medium text-sm">Above Continue Shopping</div>
              <div className="text-xs text-gray-600 mt-1">Left column, above button</div>
            </button>

            <button
              onClick={() => setSelectedOption('offer_ui_above_order_details')}
              className={`w-full text-left px-3 py-3 rounded-lg border transition-all ${
                selectedOption === 'offer_ui_above_order_details'
                  ? 'border-blue-600 bg-blue-50 text-blue-800 shadow-md'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
              }`}
              aria-pressed={selectedOption === 'offer_ui_above_order_details'}
            >
              <div className="font-medium text-sm">Above Order Details</div>
              <div className="text-xs text-gray-600 mt-1">Left column, before details</div>
            </button>

            <button
              onClick={() => setSelectedOption('offer_ui_above_map')}
              className={`w-full text-left px-3 py-3 rounded-lg border transition-all ${
                selectedOption === 'offer_ui_above_map'
                  ? 'border-blue-600 bg-blue-50 text-blue-800 shadow-md'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
              }`}
              aria-pressed={selectedOption === 'offer_ui_above_map'}
            >
              <div className="font-medium text-sm">Above Map</div>
              <div className="text-xs text-gray-600 mt-1">Left column, after thank you</div>
            </button>

            <div className="pt-3 mt-3 border-t border-gray-200">
              <p className="text-xs text-gray-500 leading-relaxed">
                💡 Try different placements to see how offers integrate into your checkout flow
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    </div>
  )
}

export default ShopifyThankYouPage